import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheckIcon, BookOpenCheckIcon, BriefcaseBusinessIcon, CalculatorIcon, CheckIcon, ClipboardListIcon, FilePenLineIcon, FileSpreadsheetIcon, GiftIcon, LandmarkIcon, MailIcon, NotebookTabsIcon, ReceiptTextIcon, ScaleIcon, SettingsIcon, ShieldCheckIcon, SparklesIcon, UsersRoundIcon, WalletCardsIcon } from 'lucide-react';
import { Accordion } from '../components/Accordion';
import { PurchaseButton } from '../components/PurchaseButton';
import { useScreenInit } from '../useScreenInit';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/gwWndnMiDvNzDBgTXN7JDK/ChatGPT_Image_Jul_12,_2026,_04_51_44_AM.png';
const modules = [{
  title: 'Expediente Digital del Trabajador',
  description: 'Organiza la información esencial de cada persona trabajadora en una estructura clara y práctica.',
  icon: <NotebookTabsIcon size={20} data-id="element-15" />
}, {
  title: 'Control de Vacaciones y Prima Vacacional',
  description: 'Lleva el control de días, saldos y cálculos para dar seguimiento con mayor claridad.',
  icon: <WalletCardsIcon size={20} data-id="element-16" />
}, {
  title: 'Aguinaldo y PTU',
  description: 'Centraliza las referencias necesarias para revisar estas prestaciones con orden.',
  icon: <CalculatorIcon size={20} data-id="element-17" />
}, {
  title: 'Asistencia, Incidencias y Reforma de Jornada',
  description: 'Documenta incidencias y mantén un seguimiento operativo de la jornada.',
  icon: <ClipboardListIcon size={20} data-id="element-18" />
}, {
  title: 'Altas, Bajas y Movimientos IMSS',
  description: 'Cuenta con una guía organizada para registrar movimientos y no perder pasos importantes.',
  icon: <FileSpreadsheetIcon size={20} data-id="element-19" />
}, {
  title: 'Calculadora de Finiquito e Indemnización',
  description: 'Una herramienta central para acompañar la revisión de cálculos y conceptos de salida.',
  icon: <ScaleIcon size={20} data-id="element-20" />
}, {
  title: 'Tracker de Onboarding y Offboarding',
  description: 'Da seguimiento a los pasos de ingreso y salida para una experiencia más ordenada.',
  icon: <UsersRoundIcon size={20} data-id="element-21" />
}];
const bonuses = [{
  title: 'BONO 1: Kit NOM-035 + Ley Silla listo para inspección',
  description: 'Material de apoyo para organizar documentación y revisar puntos relevantes antes de una inspección.',
  icon: <ShieldCheckIcon size={20} data-id="element-22" />,
  previewImage: 'https://cdn.magicpatterns.com/uploads/7bPsGeCXtTyhDnwqqp7wzd/Bono_1.png',
  previewAlt: 'Vista previa del Bono 1'
}, {
  title: 'BONO 2: Guía de Incapacidades IMSS',
  description: 'Una guía de consulta para ubicar los pasos y referencias principales de incapacidades.',
  icon: <BookOpenCheckIcon size={20} data-id="element-23" />,
  previewImage: 'https://cdn.magicpatterns.com/uploads/x37PYv3ai7Pwg9kZsdAy9G/Bono_2.png',
  previewAlt: 'Vista previa del Bono 2'
}, {
  title: 'BONO 3: Checklist Onboarding/Offboarding PDF imprimible',
  description: 'Lista imprimible para no dejar pendientes en los procesos de ingreso y salida.',
  icon: <ClipboardListIcon size={20} data-id="element-24" />,
  previewImage: 'https://cdn.magicpatterns.com/uploads/kizLwtjtdxFg3DA67Ga35F/Bono_3.png',
  previewAlt: 'Vista previa del Bono 3'
}, {
  title: 'BONO 4: 10 Plantillas Word editables (cartas, actas, constancias)',
  description: 'Plantillas editables para adaptar a los procesos internos de tu empresa.',
  icon: <FilePenLineIcon size={20} data-id="element-25" />,
  previewImage: 'https://cdn.magicpatterns.com/uploads/buTMkZ7hM7XkfZyCZ9ntSd/Bono_4.png',
  previewAlt: 'Vista previa del Bono 4'
}, {
  title: 'BONO 5: Kit de Disciplina Progresiva (4 documentos Word)',
  description: 'Documentos para llevar un proceso disciplinario con un registro más consistente.',
  icon: <LandmarkIcon size={20} data-id="element-26" />,
  previewImage: 'https://cdn.magicpatterns.com/uploads/g9QWxYWdC39pZ18CAhc35E/Bono_5.png',
  previewAlt: 'Vista previa del Bono 5'
}];
const objections = [{
  title: '¿El kit sustituye la asesoría legal o contable?',
  description: 'No. Es una herramienta de organización y consulta operativa. Para casos particulares o decisiones legales, consulta a la persona especialista correspondiente.',
  icon: <ScaleIcon size={19} data-id="element-27" />
}, {
  title: '¿Puedo usarlo si trabajo en una PyME?',
  description: 'Sí. El material está pensado para que los procesos cotidianos de RRHH sean más fáciles de organizar y dar seguimiento.',
  icon: <BriefcaseBusinessIcon size={19} data-id="element-28" />
}, {
  title: '¿Cómo recibo el contenido?',
  description: 'Al completar la compra, el acceso y la entrega se gestionan desde la página de pago.',
  icon: <ReceiptTextIcon size={19} data-id="element-29" />
}];
type MetaPixelFunction = ((...args: unknown[]) => void) & {
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
};
function initializeMetaPixel() {
  const pixelWindow = window as Window & {
    fbq?: MetaPixelFunction;
  };
  if (pixelWindow.fbq?.loaded) {
    pixelWindow.fbq('track', 'PageView');
    return;
  }
  if (!pixelWindow.fbq) {
    const queue: unknown[][] = [];
    const pixelStub = ((...args: unknown[]) => {
      queue.push(args);
    }) as MetaPixelFunction;
    pixelStub.queue = queue;
    pixelStub.loaded = true;
    pixelStub.version = '2.0';
    pixelWindow.fbq = pixelStub;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }
  pixelWindow.fbq('init', '1252242806993118');
  pixelWindow.fbq('track', 'PageView');
}
export function LandingPage() {
  useScreenInit();
  useEffect(() => {
    initializeMetaPixel();
  }, []);
  return <main className="site-shell" data-id="element-30">
      <section className="hero" aria-labelledby="hero-title" data-id="element-31">
        <div className="hero-office" aria-hidden="true" data-id="element-32" />
        <div className="container hero-content" data-id="element-33">
          <motion.h1 id="hero-title" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} data-id="element-34">
            Reduce errores administrativos y riesgos laborales sin contratar
            consultores ni hacer cursos interminables.
          </motion.h1>

          <motion.a href="#oferta" className="product-link hero-product" aria-label="Ver la oferta del Kit RRHH en Orden" initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.12
        }} data-id="element-35">
            <img src={HERO_IMAGE} alt="Kit RRHH en Orden México 2026 con herramientas, documentos y beneficios para PyMEs" data-id="element-36" />
          </motion.a>

          <p className="hero-subtitle" data-id="element-37">
            Todo lo que necesitas para gestionar personal en México
            correctamente — sin estudiar derecho laboral y sin depender del
            contador para cada duda.
          </p>

          <ul className="delivery-badges" aria-label="Características de entrega" data-id="element-38">
            <li data-id="element-39">
              <FilePenLineIcon size={17} aria-hidden="true" data-id="element-40" /> 100% PDF
            </li>
            <li data-id="element-41">
              <SparklesIcon size={17} aria-hidden="true" data-id="element-42" /> Entrega Inmediata
            </li>
            <li data-id="element-43">
              <BookOpenCheckIcon size={17} aria-hidden="true" data-id="element-44" /> 7 herramientas
              funcionales + 5 Bonos
            </li>
          </ul>
          <PurchaseButton data-id="element-45" />
          <p className="secure-note" data-id="element-46">
            <ShieldCheckIcon size={15} aria-hidden="true" data-id="element-47" /> Acceso mediante
            página de pago segura
          </p>
        </div>
      </section>

      <section className="section pain-section" aria-labelledby="pain-title" data-id="element-48">
        <div className="container narrow" data-id="element-49">
          <p className="section-kicker" data-id="element-50">Tu día a día</p>
          <h2 id="pain-title" data-id="element-51">
            El problema no es trabajar en RRHH, es resolver todo sola
          </h2>
          <div className="pain-list" data-id="element-52">
            {['Tener que buscar la respuesta correcta mientras el equipo espera una solución.', 'Llevar vacaciones, incidencias y expedientes en archivos que no conversan entre sí.', 'Dudar qué documento usar para cada movimiento, carta o situación con el personal.', 'Sentir presión por cumplir procesos sin contar con una ruta clara para ordenarlos.', 'Invertir horas en tareas repetitivas que podrían tener una estructura desde el inicio.'].map((pain, index) => <article className="pain-card" key={pain} data-id="element-53">
                <span className="pain-number" data-id="element-54">0{index + 1}</span>
                <p data-id="element-55">{pain}</p>
              </article>)}
          </div>
        </div>
      </section>

      <section className="section modules-section" aria-labelledby="modules-title" data-id="element-56">
        <div className="container narrow" data-id="element-57">
          <p className="section-kicker" data-id="element-58">Una base para tu operación</p>
          <h2 id="modules-title" data-id="element-59">Para eso hicimos este kit</h2>
          <p className="section-intro" data-id="element-60">
            Herramientas pensadas para acompañar las tareas que más se repiten
            en tu área.
          </p>
          <Accordion items={modules} data-id="element-61" />
          <div className="center-action" data-id="element-62">
            <PurchaseButton data-id="element-63" />
          </div>
        </div>
      </section>

      <section className="section feedback-section" aria-labelledby="feedback-title" data-id="element-64">
        <div className="container narrow" data-id="element-65">
          <p className="section-kicker" data-id="element-66">Cómo podría ayudarte</p>
          <h2 id="feedback-title" data-id="element-67">Feedback de uso: ejemplos de situaciones</h2>
          <p className="section-intro" data-id="element-68">
            Estos son ejemplos de comentarios representativos, no reseñas
            verificadas de clientes.
          </p>
          <div className="feedback-grid" data-id="element-69">
            {[['“Ya no tengo que empezar desde cero cada vez que solicitan una constancia.”', 'Analista de RRHH · PyME'], ['“Me ayudaría a organizar los pendientes de ingreso sin omitir pasos.”', 'Generalista de talento · Servicios'], ['“Tener una referencia para vacaciones me ayudaría a responder con mayor seguridad.”', 'Encargada administrativa · Comercio']].map(([quote, role]) => <figure className="feedback-card" key={quote} data-id="element-70">
                <div className="quote-mark" aria-hidden="true" data-id="element-71">
                  “
                </div>
                <blockquote data-id="element-72">{quote}</blockquote>
                <figcaption data-id="element-73">{role}</figcaption>
              </figure>)}
          </div>
        </div>
      </section>

      <section className="section bonus-section" aria-labelledby="bonus-title" data-id="element-74">
        <div className="container narrow" data-id="element-75">
          <p className="section-kicker" data-id="element-76">Material complementario</p>
          <h2 id="bonus-title" data-id="element-77">Además incluido con tu kit</h2>
          <p className="bonus-note" data-id="element-78">
            <GiftIcon size={17} aria-hidden="true" data-id="element-79" /> Los bonos se incluyen sin
            costo adicional con la compra del Kit.
          </p>
          <Accordion items={bonuses} data-id="element-80" />
        </div>
      </section>

      <section className="section comparison-section" aria-labelledby="comparison-title" data-id="element-81">
        <div className="container narrow" data-id="element-82">
          <p className="section-kicker" data-id="element-83">Un cambio tangible</p>
          <h2 id="comparison-title" data-id="element-84">
            Sin Kit RRHH en Orden vs Con Kit RRHH en Orden
          </h2>
          <div className="comparison-table" role="table" aria-label="Comparación del flujo de trabajo" data-id="element-85">
            <div className="comparison-column comparison-column--without" role="rowgroup" data-id="element-86">
              <h3 role="columnheader" data-id="element-87">Sin Kit RRHH en Orden</h3>
              <ul data-id="element-88">
                <li data-id="element-89">Buscar formatos cada vez</li>
                <li data-id="element-90">Procesos dispersos</li>
                <li data-id="element-91">Más tiempo resolviendo dudas</li>
                <li data-id="element-92">Seguimiento manual de pendientes</li>
              </ul>
            </div>
            <div className="comparison-column comparison-column--with" role="rowgroup" data-id="element-93">
              <h3 role="columnheader" data-id="element-94">Con Kit RRHH en Orden</h3>
              <ul data-id="element-95">
                <li data-id="element-96">Material organizado para consultar</li>
                <li data-id="element-97">Herramientas en un solo lugar</li>
                <li data-id="element-98">Mayor claridad para priorizar</li>
                <li data-id="element-99">Checklists para dar seguimiento</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="offer-section" id="oferta" aria-labelledby="offer-title" data-id="element-100">
        <div className="container offer-container" data-id="element-101">
          <div className="offer-heading" data-id="element-102">
            <p className="eyebrow" data-id="element-103">
              <span data-id="element-104" /> Kit digital para tu operación
            </p>
            <h2 id="offer-title" data-id="element-105">Pon orden en tu área de RRHH</h2>
            <p data-id="element-106">
              Una compra, un solo lugar para organizar herramientas, documentos
              y procesos cotidianos.
            </p>
          </div>
          <div className="offer-card" data-id="element-107">
            <div className="offer-card-top" data-id="element-108">
              <div data-id="element-109">
                <p className="summary-label" data-id="element-110">Resumen de tu pedido</p>
                <h3 data-id="element-111">Kit RRHH en Orden</h3>
                <p className="summary-items" data-id="element-112">
                  20+ herramientas y recursos para organizar tu operación
                </p>
              </div>
              <BadgeCheckIcon size={30} aria-hidden="true" data-id="element-113" />
            </div>
            <ul className="order-summary-grid" aria-label="Contenido incluido en el kit" data-id="element-114">
              <li data-id="element-115">
                <CheckIcon size={15} aria-hidden="true" data-id="element-116" />
                <span data-id="element-117">
                  <strong data-id="element-118">7</strong> herramientas Excel funcionales
                </span>
              </li>
              <li data-id="element-119">
                <CheckIcon size={15} aria-hidden="true" data-id="element-120" />
                <span data-id="element-121">
                  <strong data-id="element-122">4</strong> PDFs informativos
                </span>
              </li>
              <li data-id="element-123">
                <CheckIcon size={15} aria-hidden="true" data-id="element-124" />
                <span data-id="element-125">
                  <strong data-id="element-126">10</strong> plantillas Word editables
                </span>
              </li>
              <li data-id="element-127">
                <CheckIcon size={15} aria-hidden="true" data-id="element-128" />
                <span data-id="element-129">
                  <strong data-id="element-130">4</strong> documentos de disciplina progresiva
                </span>
              </li>
              <li data-id="element-131">
                <CheckIcon size={15} aria-hidden="true" data-id="element-132" />
                <span data-id="element-133">Recursos de onboarding y offboarding</span>
              </li>
            </ul>
            <div className="price-row" data-id="element-134">
              <div data-id="element-135">
                <span className="old-price" data-id="element-136">$50 USD</span>
                <strong data-id="element-137">
                  $17 <small data-id="element-138">USD</small>
                </strong>
              </div>
              <span className="one-time" data-id="element-139">Pago único</span>
            </div>
            <PurchaseButton className="purchase-button--wide" label="Quiero mi kit RRHH ahora" data-id="element-140" />
            <p className="payment-note" data-id="element-141">
              <CheckIcon size={15} aria-hidden="true" data-id="element-142" /> Un solo pago para
              acceder a todo el paquete detallado arriba.
            </p>
          </div>
        </div>
      </section>

      <section className="section faq-section" aria-labelledby="faq-title" data-id="element-143">
        <div className="container narrow" data-id="element-144">
          <p className="section-kicker" data-id="element-145">Antes de decidir</p>
          <h2 id="faq-title" data-id="element-146">Preguntas frecuentes</h2>
          <Accordion items={objections} data-id="element-147" />
        </div>
      </section>

      <footer className="footer" data-id="element-148">
        <div className="container footer-content" data-id="element-149">
          <div className="support-mark" aria-hidden="true" data-id="element-150">
            <SettingsIcon size={25} data-id="element-151" />
            <MailIcon size={12} data-id="element-152" />
          </div>
          <div data-id="element-153">
            <p data-id="element-154">Soporte</p>
            <a href="mailto:EbookDreamShop@gmail.com" data-id="element-155">
              EbookDreamShop@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </main>;
}